// Client management, self-service domains, project-client associations API
import { api } from './client';

// ============================================================================
// CLIENT MANAGEMENT API
// ============================================================================

export interface ClientListParams {
  active?: boolean;
  search?: string;
}

export interface ContactListParams {
  active?: boolean;
}

export interface BulkDomainResult {
  added_count: number;
  duplicate_count: number;
  invalid_count: number;
  added: string[];
  duplicates: string[];
  invalid: { input: string; reason: string }[];
  domains: any[];
}

export interface BulkNetblockResult {
  added_count: number;
  duplicate_count: number;
  invalid_count: number;
  added: { cidr: string; netname: string; registrant: string; rir: string; asn: string; country: string }[];
  duplicates: string[];
  invalid: { input: string; reason: string }[];
  own_netblocks: string;
}

export const clientsAPI = {
  getAll: (params?: ClientListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.active !== undefined) queryParams.append('active', String(params.active));
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('🏢 Fetching all clients', queryString);
    return api.get(`/clients${queryString}`);
  },

  getById: (clientId: number) => {
    console.log('🏢 Fetching client:', clientId);
    return api.get(`/clients/${clientId}`);
  },

  create: (data: any) => {
    console.log('➕ Creating client:', data.name);
    return api.post('/clients', data);
  },

  update: (clientId: number, data: any) => {
    console.log('📝 Updating client:', clientId, data);
    return api.put(`/clients/${clientId}`, data);
  },

  delete: (clientId: number, hard?: boolean) => {
    const queryString = hard ? '?hard=true' : '';
    console.log('🗑️ Deleting client:', clientId, hard ? '(hard delete)' : '');
    return api.delete(`/clients/${clientId}${queryString}`);
  },

  // Client Contacts
  getContacts: (clientId: number, params?: ContactListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.active !== undefined) queryParams.append('active', String(params.active));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('👤 Fetching contacts for client:', clientId);
    return api.get(`/clients/${clientId}/contacts${queryString}`);
  },

  getContact: (clientId: number, contactId: number) => {
    console.log('👤 Fetching contact:', contactId, 'for client:', clientId);
    return api.get(`/clients/${clientId}/contacts/${contactId}`);
  },

  createContact: (clientId: number, data: any) => {
    console.log('➕ Creating contact for client:', clientId, data);
    return api.post(`/clients/${clientId}/contacts`, data);
  },

  updateContact: (clientId: number, contactId: number, data: any) => {
    console.log('📝 Updating contact:', contactId, data);
    return api.put(`/clients/${clientId}/contacts/${contactId}`, data);
  },

  deleteContact: (clientId: number, contactId: number, hard?: boolean) => {
    const queryString = hard ? '?hard=true' : '';
    console.log('🗑️ Deleting contact:', contactId, hard ? '(hard delete)' : '');
    return api.delete(`/clients/${clientId}/contacts/${contactId}${queryString}`);
  },

  createContactUser: (clientId: number, contactId: number, password: string) => {
    console.log('👤 Creating user account for contact:', contactId);
    return api.post(`/clients/${clientId}/contacts/${contactId}/create-user`, { password });
  },

  // Domain Management
  getDomains: (clientId: number) => {
    console.log('🌐 Fetching domains for client:', clientId);
    return api.get(`/clients/${clientId}/domains`);
  },

  createDomain: (clientId: number, data: { domain: string; is_base?: boolean; description?: string }) => {
    console.log('➕ Creating domain for client:', clientId, data);
    return api.post(`/admin/clients/${clientId}/domains`, data);
  },

  bulkCreateDomains: (clientId: number, domains: string[], description?: string) => {
    console.log('📦 Bulk creating domains for client:', clientId, domains.length);
    return api.post<BulkDomainResult>(`/admin/clients/${clientId}/domains/bulk`, { domains, description });
  },

  bulkAddNetblocks: (clientId: number, ranges: string[]) => {
    console.log('📦 Bulk adding own netblocks for client:', clientId, ranges.length);
    return api.post<BulkNetblockResult>(`/admin/clients/${clientId}/netblocks/bulk`, { ranges });
  },

  updateDomain: (clientId: number, domainId: number, data: { domain?: string; is_base?: boolean; description?: string; active?: boolean }) => {
    console.log('📝 Updating domain:', domainId, data);
    return api.put(`/admin/clients/${clientId}/domains/${domainId}`, data);
  },

  deleteDomain: (clientId: number, domainId: number) => {
    console.log('🗑️ Deleting domain:', domainId);
    return api.delete(`/admin/clients/${clientId}/domains/${domainId}`);
  },

  updateDomainSettings: (clientId: number, enforceDomainScope: boolean) => {
    console.log('⚙️ Updating domain settings for client:', clientId, { enforce_domain_scope: enforceDomainScope });
    return api.put(`/admin/clients/${clientId}/domain-settings`, { enforce_domain_scope: enforceDomainScope });
  },
};

// ============================================================================
// CLIENT SELF-SERVICE DOMAIN MANAGEMENT API
// ============================================================================

export interface MyDomain {
  id: number;
  client_id: number;
  domain: string;
  is_base: boolean;
  description: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  breach_result_count: number;
  is_breach_monitored: boolean;
  next_breach_scan_at?: string;
}

export interface MyDomainsResponse {
  domains: MyDomain[];
  domain_limit: number;
  domain_count: number;
}

export interface DomainDeleteInfo {
  domain: string;
  is_base: boolean;
  is_breach_monitored: boolean;
  breach_result_count: number;
  breach_domain_id: number;
  warning: boolean;
}

export const myDomainsAPI = {
  getMyDomains: () => {
    return api.get<MyDomainsResponse>('/my-domains');
  },

  createDomain: (data: { domain: string; description?: string }) => {
    return api.post<MyDomain>('/my-domains', data);
  },

  updateDomain: (domainId: number, data: { domain?: string; description?: string; active?: boolean }) => {
    return api.put<MyDomain>(`/my-domains/${domainId}`, data);
  },

  getDeleteInfo: (domainId: number) => {
    return api.get<DomainDeleteInfo>(`/my-domains/${domainId}/delete-info`);
  },

  deleteDomain: (domainId: number) => {
    return api.delete(`/my-domains/${domainId}`);
  },

  setBaseDomain: (domainId: number) => {
    return api.post<MyDomain>(`/my-domains/${domainId}/set-base`);
  },
};

// ============================================================================
// PROJECT-CLIENT ASSOCIATION API
// ============================================================================

export const projectClientsAPI = {
  getContacts: (projectId: number) => {
    console.log('👥 Fetching notification contacts for project:', projectId);
    return api.get(`/projects/${projectId}/client-contacts`);
  },

  addContact: (projectId: number, contactId: number) => {
    console.log('➕ Adding contact to project:', projectId, 'contact:', contactId);
    return api.post(`/projects/${projectId}/client-contacts`, { contact_id: contactId });
  },

  removeContact: (projectId: number, contactId: number) => {
    console.log('➖ Removing contact from project:', projectId, 'contact:', contactId);
    return api.delete(`/projects/${projectId}/client-contacts/${contactId}`);
  },

  updateSettings: (projectId: number, data: any) => {
    console.log('⚙️ Updating project client settings:', projectId, data);
    return api.put(`/projects/${projectId}/client-settings`, data);
  },
};

// ============================================================================
// CLIENT EVENT TEMPLATES API
// ============================================================================

export interface ClientEventTemplate {
  template_id: string;
  template_name: string;
  enabled: boolean;
  custom_subject?: string;
  custom_body?: string;
  variables?: string[];
}

export const clientEventTemplatesAPI = {
  getClientTemplates: (clientId: number) => {
    console.log('📧 Fetching event templates for client:', clientId);
    return api.get<ClientEventTemplate[]>(`/clients/${clientId}/event-templates`);
  },

  bulkUpdateTemplates: (clientId: number, templates: ClientEventTemplate[]) => {
    console.log('📦 Bulk updating event templates for client:', clientId);
    return api.put(`/clients/${clientId}/event-templates`, { templates });
  },

  updateTemplate: (clientId: number, templateId: string, data: Partial<ClientEventTemplate>) => {
    console.log('✏️ Updating event template:', templateId);
    return api.put<ClientEventTemplate>(`/clients/${clientId}/event-templates/${templateId}`, data);
  },

  deleteTemplate: (clientId: number, templateId: string) => {
    console.log('🗑️ Resetting event template to default:', templateId);
    return api.delete(`/clients/${clientId}/event-templates/${templateId}`);
  },

  getContactsForAlertManagement: (clientId: number) => {
    console.log('👥 Fetching contacts for alert management:', clientId);
    return api.get<{ contacts: ContactAlertSummary[]; count: number }>(`/clients/${clientId}/contacts/alert-management`);
  },

  getContactTemplates: (clientId: number, contactId: number) => {
    console.log('📧 Fetching event templates for contact:', contactId);
    return api.get<{ templates: ContactEventTemplate[]; contact: any; alerts_locked: boolean }>(`/clients/${clientId}/contacts/${contactId}/event-templates`);
  },

  updateContactTemplate: (clientId: number, contactId: number, templateId: string, data: any) => {
    console.log('✏️ Updating contact event template:', templateId);
    return api.put(`/clients/${clientId}/contacts/${contactId}/event-templates/${templateId}`, data);
  },

  bulkUpdateContactTemplates: (clientId: number, contactId: number, settings: any[]) => {
    console.log('📦 Bulk updating contact event templates:', contactId);
    return api.put(`/clients/${clientId}/contacts/${contactId}/event-templates`, { settings });
  },

  updateContactAlertsLock: (clientId: number, contactId: number, locked: boolean) => {
    console.log('🔒 Updating contact alerts lock:', contactId, locked);
    return api.put(`/clients/${clientId}/contacts/${contactId}/alerts-lock`, { locked });
  },
};

export interface ContactAlertSummary {
  id: number;
  client_id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_primary: boolean;
  vacation_mode: boolean;
  alerts_locked: boolean;
  enabled_alerts: number;
  total_alerts: number;
  can_toggle_alerts: boolean;
  user_id?: number;
}

export interface ContactEventTemplate {
  template: string;
  description: string;
  setting: ClientEventTemplate | null;
  is_contact_specific: boolean;
}

export interface EventTemplate {
  id: number;
  name: string;
  event_type: string;
  subject: string;
  body: string;
  delivery_method: 'email' | 'portal' | 'webhook';
  enabled: boolean;
  variables?: Record<string, string>;
}

export const clientEventsAPI = {
  getEventTemplates: (clientId: number) => {
    console.log('📧 Fetching event templates for client:', clientId);
    return api.get<EventTemplate[]>(`/clients/${clientId}/event-templates`);
  },

  createEventTemplate: (clientId: number, data: Partial<EventTemplate>) => {
    console.log('➕ Creating event template for client:', clientId);
    return api.post<EventTemplate>(`/clients/${clientId}/event-templates`, data);
  },

  updateEventTemplate: (clientId: number, templateId: number, data: Partial<EventTemplate>) => {
    console.log('✏️ Updating event template:', templateId);
    return api.put<EventTemplate>(`/clients/${clientId}/event-templates/${templateId}`, data);
  },

  deleteEventTemplate: (clientId: number, templateId: number) => {
    console.log('🗑️ Deleting event template:', templateId);
    return api.delete(`/clients/${clientId}/event-templates/${templateId}`);
  },
};
